// Where "Open in Outlook" opens an email, chosen per device (Brett, 2026-10-16): the
// desktop app suits a Windows laptop, the web suits a phone or Mac. Kept in this
// browser only, so each device keeps its own choice.
//
// Desktop links (2026-10-20, tested on Brett's laptop): a website can't open classic
// Outlook by itself, so each Windows computer installs a small link handler once
// (Settings → "Set up desktop links", a .reg file for the current user only, no admin).
// Links look like `simplefee:open?mid=<Message-ID>`; the handler asks the running
// Outlook to find that Message-ID in every mailbox (Inbox and Sent first, then all
// folders) and opens it. Message-IDs work with several mailboxes in one Outlook, unlike
// Microsoft's converted item ids, which didn't match his second mailbox.

export type OutlookOpen = 'web' | 'desktop';
const KEY = 'simplefee.outlookOpen';

export function getOutlookOpen(): OutlookOpen {
  try {
    return localStorage.getItem(KEY) === 'desktop' ? 'desktop' : 'web';
  } catch {
    return 'web';
  }
}

export function setOutlookOpen(v: OutlookOpen) {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    // Private window or blocked storage: the default (web) still works.
  }
}

export const isWindows = () => typeof navigator !== 'undefined' && /Windows/i.test(navigator.userAgent);

/** `simplefee:open?mid=…` for an email's Message-ID (`<…@…>`). Apostrophes are encoded too. */
export function desktopLink(messageId: string): string {
  return `simplefee:open?mid=${encodeURIComponent(messageId).replace(/'/g, '%27')}`;
}

/**
 * The handler Windows runs for a simplefee: link (`%1` = the whole link). One line of
 * PowerShell with no double quotes or backslashes, so it sits safely inside a .reg string.
 */
export const HANDLER_SCRIPT = [
  "$u='%1'",
  "$mid=[uri]::UnescapeDataString(($u -split 'mid=',2)[1])",
  '$q=[char]34',
  '$a=[char]39',
  "$f='@SQL='+$q+'http://schemas.microsoft.com/mapi/proptag/0x1035001F'+$q+' = '+$a+($mid -replace $a,($a+$a))+$a",
  '$o=New-Object -ComObject Outlook.Application',
  'function Look($fl){try{$x=$fl.Items.Find($f);if($x){return $x}}catch{};foreach($c in $fl.Folders){$x=Look $c;if($x){return $x}};return $null}',
  '$h=$null',
  'foreach($s in $o.Session.Stores){foreach($n in 6,5){try{$h=$s.GetDefaultFolder($n).Items.Find($f)}catch{};if($h){break}};if($h){break}}',
  'if(-not $h){foreach($s in $o.Session.Stores){try{$h=Look $s.GetRootFolder()}catch{};if($h){break}}}',
  "if($h){$h.Display();try{$h.GetInspector.Activate()}catch{}}else{(New-Object -ComObject WScript.Shell).Popup('SimpleFee could not find this email in Outlook. It may have been deleted.',0,'SimpleFee',48)|Out-Null}",
].join(';');

/** What Windows runs for a simplefee: link. */
export const HANDLER_COMMAND = `powershell.exe -NoProfile -NonInteractive -WindowStyle Hidden -ExecutionPolicy Bypass -Command "${HANDLER_SCRIPT}"`;

const regString = (s: string) => s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
const psString = (s: string) => `'${s.replace(/'/g, "''")}'`;
const KEY_PS = 'HKCU:\\Software\\Classes\\simplefee';

/**
 * The same setup as one line to paste into PowerShell. Main route since 2026-10-20: Windows 11
 * Smart App Control blocks downloaded .reg files (Brett's laptop), but not pasted commands.
 */
export function setupCommand(): string {
  return [
    `New-Item -Path '${KEY_PS}\\shell\\open\\command' -Force | Out-Null`,
    `Set-ItemProperty -Path '${KEY_PS}' -Name '(default)' -Value 'URL:SimpleFee CRE'`,
    `Set-ItemProperty -Path '${KEY_PS}' -Name 'URL Protocol' -Value ''`,
    `Set-ItemProperty -Path '${KEY_PS}\\shell\\open\\command' -Name '(default)' -Value ${psString(HANDLER_COMMAND)}`,
    `'SimpleFee desktop links are set up. You can close this window.'`,
  ].join('; ');
}

export const removeCommand = () => `Remove-Item -Path '${KEY_PS}' -Recurse -ErrorAction SilentlyContinue; 'SimpleFee desktop links removed.'`;

/** The one-time setup file: registers simplefee: links for the current Windows user. */
export function setupRegFile(): string {
  const command = HANDLER_COMMAND;
  return [
    'Windows Registry Editor Version 5.00',
    '',
    '; SimpleFee CRE: opens "Open in Outlook" links in classic Outlook on this computer.',
    '; Affects only your Windows user. Remove it any time with the remove file in Settings.',
    '',
    '[HKEY_CURRENT_USER\\Software\\Classes\\simplefee]',
    '@="URL:SimpleFee CRE"',
    '"URL Protocol"=""',
    '',
    '[HKEY_CURRENT_USER\\Software\\Classes\\simplefee\\shell\\open\\command]',
    `@="${regString(command)}"`,
    '',
  ].join('\r\n');
}

export function removeRegFile(): string {
  return ['Windows Registry Editor Version 5.00', '', '[-HKEY_CURRENT_USER\\Software\\Classes\\simplefee]', ''].join('\r\n');
}

export function download(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/octet-stream' }));
  const a = Object.assign(document.createElement('a'), { href: url, download: filename });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
